import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarClock, CheckCircle2, Send } from 'lucide-react';
import { useLocalized } from '@/hooks/useLocalized';
import { cn } from '@/lib/cn';
import { formatSom } from '@/lib/format';
import { useAuthStore } from '@/store/auth';
import { monthlyInstallment, PURCHASE_CONTACT_URL } from './purchaseContact';

interface Props {
  kind: 'course' | 'career';
  title: string;
  priceUzs?: number;
  owned: boolean;
  className?: string;
}

/**
 * Kurs/kasb narxi va «Sotib olish» (Telegram xabari tayyor matn bilan). Sotib olingan bo'lsa —
 * belgi. Narx qo'yilmagan (sotuvda emas) va sotib olinmagan bo'lsa hech narsa ko'rsatilmaydi.
 */
export function PurchaseBox({ kind, title, priceUzs, owned, className }: Props) {
  const { t } = useTranslation();
  const { lang } = useLocalized();
  const user = useAuthStore((s) => s.user);

  if (owned) {
    return (
      <div className={cn('inline-flex items-center gap-2 text-sm font-medium text-[var(--easy)]', className)}>
        <CheckCircle2 className="h-5 w-5" />
        {t(kind === 'course' ? 'courses.purchase.ownedCourse' : 'courses.purchase.ownedCareer')}
      </div>
    );
  }
  if (priceUzs == null) return null;

  const price = formatSom(priceUzs, lang);
  const monthly = monthlyInstallment(priceUzs);
  const href = `${PURCHASE_CONTACT_URL}?text=${encodeURIComponent(
    t('courses.purchase.message', {
      item: t(kind === 'course' ? 'courses.purchase.itemCourse' : 'courses.purchase.itemCareer', { title }),
      price,
      account: user ? `${user.email} (@${user.username})` : '',
      interpolation: { escapeValue: false },
    }),
  )}`;

  return (
    <div className={cn('flex flex-wrap items-center gap-x-5 gap-y-3', className)}>
      <div>
        <div className="text-2xl font-bold">{price}</div>
        <div className="text-xs text-content-muted">
          {t(kind === 'course' ? 'courses.purchase.lifetime' : 'courses.purchase.careerIncludes')}
        </div>
        {monthly != null && (
          <div className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-brand-600 dark:text-brand-400">
            <CalendarClock className="h-3.5 w-3.5" aria-hidden />
            {t('courses.purchase.installmentNote', { amount: formatSom(monthly, lang) })}
          </div>
        )}
      </div>
      {user ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary inline-flex gap-2 px-6 py-3 text-sm font-semibold"
        >
          <Send className="h-4 w-4" />
          {t('courses.purchase.buy')}
        </a>
      ) : (
        <Link to="/login" className="btn btn-primary inline-flex gap-2 px-6 py-3 text-sm font-semibold">
          {t('courses.purchase.loginToBuy')}
        </Link>
      )}
    </div>
  );
}
